/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React, { useEffect, useState, ReactElement } from "react";

import { useDispatch, useSelector } from "react-redux";
import { useHistory, Redirect } from "react-router-dom";

import Pagination from "rc-pagination";

import {
  DetailsList,
  DetailsListLayoutMode,
  Dropdown,
  IColumn,
  IDetailsRowProps,
  Link,
  SelectionMode,
  Spinner,
  SpinnerSize,
  TooltipHost,
} from "@fluentui/react";

import { useAuth } from "Authentication/AuthProvider";
import ChevronIcon from "Components/Common/ChevronIcon/ChevronIcon";
import { PaginationRow } from "Components/Common/Pagination/Pagination.styles";
import { detailListStyles, spinnerStyles } from "Components/Common/SharedListStyles.styles";
import StatusElement from "Components/Common/Status/StatusElement";
import { SortingColumns } from "Constants/AdminConstants";
import {
  userProfileSearchCurrentPageUpdate,
  userProfileSearchPageSizeUpdate,
  userProfileSearchSortingUpdate,
} from "Dataflows/MVP/MVPAction";
import {
  getFilteredCount,
  getFilteredUserProfiles,
  getIsLoadingStatus,
  getPaginationCurrentPage,
  getPaginationPageSize,
} from "Dataflows/MVP/Search/MVPUserProfileSearchSelector";
import { formatDate } from "Utility/FormatHelper";

const headerTooltip = (columnName: string): React.ReactElement => {
  return (
    <TooltipHost id={columnName} content={columnName}>
      <span>{columnName}</span>
    </TooltipHost>
  );
};

const idLink = (id: number): React.ReactElement => {
  return <Link>{id}</Link>;
};

const formatStatusColumn = (statusKey: string): React.ReactElement => {
  return <StatusElement statusValue={statusKey} />;
};

/**
 * The Most Valuable Professional List component
 * @method
 * @returns {ReactElement} The Most Valuable Professional List component
 */
const MostValuableProfessionalList = (): ReactElement => {
  const history = useHistory();
  const dispatch = useDispatch();
  const userProfiles = useSelector(getFilteredUserProfiles);
  const totalCount = useSelector(getFilteredCount);
  const isLoadingUsers = useSelector(getIsLoadingStatus);
  const { isUserLoggedIn, loading, hasAnyRoles } = useAuth();

  const [columns, setColumns] = useState<IColumn[]>([
    {
      key: "id",
      name: "ID",
      ariaLabel: "ID",
      fieldName: "id",
      minWidth: 30,
      isResizable: true,
      onRender: (item, _index, _column): React.ReactElement => idLink(item.id),
    },
    {
      key: "firstName",
      name: "First Name",
      ariaLabel: "First Name",
      fieldName: "firstName",
      minWidth: 80,
      isResizable: true,
      onRenderHeader: (props): React.ReactElement => headerTooltip(props?.column?.name || ""),
    },
    {
      key: "lastName",
      name: "Last Name",
      ariaLabel: "Last name",
      fieldName: "lastName",
      minWidth: 80,
      isResizable: true,
      onRenderHeader: (props): React.ReactElement => headerTooltip(props?.column?.name || ""),
    },
    {
      key: "technologyCategory1",
      name: "Award Category #1",
      ariaLabel: "Award Category #1",
      fieldName: "technologyCategory1",
      minWidth: 100,
      isResizable: true,
      onRenderHeader: (props): React.ReactElement => headerTooltip(props?.column?.name || ""),
    },
    {
      key: "technologyArea1",
      name: "Technology Area #1",
      ariaLabel: "Technology Area #1",
      fieldName: "technologyArea1",
      minWidth: 100,
      isResizable: true,
      onRenderHeader: (props): React.ReactElement => headerTooltip(props?.column?.name || ""),
    },
    {
      key: "technologyCategory2",
      name: "Award Category #2",
      ariaLabel: "Award Category #2",
      fieldName: "technologyCategory2",
      minWidth: 100,
      isResizable: true,
      onRenderHeader: (props): React.ReactElement => headerTooltip(props?.column?.name || ""),
    },
    {
      key: "technologyArea2",
      name: "Technology Area #2",
      ariaLabel: "Technology Area #2",
      fieldName: "technologyArea2",
      minWidth: 100,
      isResizable: true,
      onRenderHeader: (props): React.ReactElement => headerTooltip(props?.column?.name || ""),
    },
    {
      key: "ndaStatus",
      name: "NDA Status",
      ariaLabel: "NDA Status",
      fieldName: "ndaStatus",
      minWidth: 70,
      isResizable: true,
      onRenderHeader: (props): React.ReactElement => headerTooltip(props?.column?.name || ""),
    },
    {
      key: "siteEmailAddress",
      name: "Site Email",
      ariaLabel: "Site Email",
      fieldName: "siteEmailAddress",
      minWidth: 100,
      isResizable: true,
      onRenderHeader: (props): React.ReactElement => headerTooltip(props?.column?.name || ""),
    },
    {
      key: "country",
      name: "Country",
      ariaLabel: "Country",
      fieldName: "countryRegion",
      minWidth: 70,
      isResizable: true,
      onRenderHeader: (props): React.ReactElement => headerTooltip(props?.column?.name || ""),
    },
    {
      key: "cpm",
      name: "CPM",
      ariaLabel: "CPM",
      fieldName: "cpmEmailAddress",
      minWidth: 50,
      isResizable: true,
      onRenderHeader: (props): React.ReactElement => headerTooltip(props?.column?.name || ""),
    },
    {
      key: "lastInvitedDate",
      name: "Last Invited Date",
      ariaLabel: "Last Invited Date",
      fieldName: "lastInvitedDate",
      minWidth: 90,
      isResizable: true,
      onRenderHeader: (props): React.ReactElement => headerTooltip(props?.column?.name || ""),
      onRender: (item) => formatDate(item.lastInvitedDate),
    },
    {
      key: "applicationProfileStatus",
      name: "Application Status",
      ariaLabel: "Application status",
      fieldName: "applicationStatus",
      minWidth: 70,
      isResizable: true,
      onRenderHeader: (props): React.ReactElement => headerTooltip(props?.column?.name || ""),
      onRender: (item: any) => formatStatusColumn(item.applicationStatus),
    },
    {
      key: "programStatus",
      name: "Program Status",
      ariaLabel: "Program status",
      fieldName: "programProfileStatus",
      minWidth: 70,
      isResizable: true,
      onRenderHeader: (props): React.ReactElement => headerTooltip(props?.column?.name || ""),
      onRender: (item: any) => formatStatusColumn(item.programProfileStatus),
    },
  ]);

  const pageSize: number = useSelector(getPaginationPageSize);
  const currentPage: number = useSelector(getPaginationCurrentPage);

  const onUserProfileSelected = (userProfileId: number): void => {
    history.push(`/mvp/details/${userProfileId}`);
  };

  const onSortingUpdate = (sortColumn?: string, sortDescending?: boolean): void => {
    dispatch(userProfileSearchSortingUpdate(sortColumn, sortDescending));
  };

  const onColumnClickHandler = (ev: React.MouseEvent<HTMLElement>, column: IColumn): void => {
    columns.forEach((col: IColumn) => {
      if (col.key == column.key) {
        const isSortedDescending = !col.isSortedDescending;
        col.isSortedDescending = isSortedDescending;
        col.isSorted = true;
        onSortingUpdate(col.fieldName, isSortedDescending);
      } else {
        col.isSorted = false;
        col.isSortedDescending = true;
      }
    });
  };

  useEffect(() => {
    const newColumns: IColumn[] = [];
    const sortingColumns = [
      SortingColumns.FirstName,
      SortingColumns.LastName,
      "ndaStatus",
      SortingColumns.Country,
      "lastInvitedDate",
    ];
    columns.forEach((col) => {
      if (sortingColumns.filter((c) => c === col.fieldName).length > 0) {
        col.onColumnClick = onColumnClickHandler;
      }
      newColumns.push(col);
    });
    setColumns(newColumns);
  }, []);

  if (isUserLoggedIn && !hasAnyRoles && !loading) {
    return <Redirect to="/unauthorized" />;
  }

  const onRenderRow = (props?: IDetailsRowProps, defaultRender?: any): React.ReactElement => {
    const profileId = props?.item.id;
    return defaultRender({
      ...props,
      onClick: () => onUserProfileSelected(profileId),
    });
  };

  if (isLoadingUsers) {
    return <Spinner size={SpinnerSize.large} styles={spinnerStyles} />;
  }

  return (
    <>
      <DetailsList
        items={userProfiles}
        columns={columns}
        selectionMode={SelectionMode.none}
        layoutMode={DetailsListLayoutMode.fixedColumns}
        isHeaderVisible={true}
        styles={detailListStyles}
        onRenderRow={onRenderRow}
        enterModalSelectionOnTouch={true}
      />
      <PaginationRow>
        <Pagination
          total={totalCount}
          onChange={(page): void => {
            dispatch(userProfileSearchCurrentPageUpdate(page));
          }}
          showTitle={false}
          current={currentPage}
          prevIcon={ChevronIcon("ChevronLeft")}
          nextIcon={ChevronIcon("ChevronRight")}
          pageSize={pageSize}
          showSizeChanger
        />
        <Dropdown
          options={[
            { key: 16, text: "Show 16 results" },
            { key: 32, text: "Show 32 results" },
            { key: 48, text: "Show 48 results" },
          ]}
          onChange={(_e, op): void => {
            dispatch(userProfileSearchPageSizeUpdate(Number(op?.key)));
          }}
          selectedKey={pageSize}
          className="pagination-dropdown"
          title="Page Size Dropdown"
        />
      </PaginationRow>
    </>
  );
};

export default MostValuableProfessionalList;
